import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

const DebugLogContext = createContext(null)

export const DebugLogProvider = ({ enabled, children }) => {
  const [logs, setLogs] = useState([])
  const logsRef = useRef([])

  const addUILog = useCallback(
    (message, data) => {
      if (!enabled) return
      const timestamp = new Date().toLocaleTimeString()
      const entry =
        data !== undefined
          ? `[${timestamp}] ${message} ${JSON.stringify(data)}`
          : `[${timestamp}] ${message}`
      logsRef.current = [...logsRef.current, entry].slice(-300)
      setLogs([...logsRef.current])
    },
    [enabled],
  )

  // Intercept console.debug to capture logs from non-hook code (e.g. natura.js, openWindow)
  useEffect(() => {
    if (!enabled) return

    const originalDebug = console.debug
    console.debug = (...args) => {
      originalDebug.apply(console, args)
      const message = args
        .map((arg) => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg)))
        .join(' ')
      const timestamp = new Date().toLocaleTimeString()
      const entry = `[${timestamp}] ${message}`
      logsRef.current = [...logsRef.current, entry].slice(-300)
      setLogs([...logsRef.current])
    }

    return () => {
      console.debug = originalDebug
    }
  }, [enabled])

  return (
    <DebugLogContext.Provider value={{ logs, addUILog, enabled }}>
      {children}
    </DebugLogContext.Provider>
  )
}

DebugLogProvider.propTypes = {
  enabled: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
}

export const useDebugLog = () => {
  const context = useContext(DebugLogContext)
  if (!context) {
    return { logs: [], addUILog: () => {}, enabled: false }
  }
  return context
}
